/**
 * Quick Customer Form Component
 *
 * Lightweight form for quickly adding a new customer from within other forms.
 */

import { useState, useCallback } from 'react';

import type { Party } from '@/gen/proto';

import { useBaseCurrency, useEnabledCurrencies } from '../../hooks/use-currencies';
import { firstNonBlank } from '../../utils/formatters';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Modal } from '../ui/modal';
import { Select } from '../ui/select';


export interface QuickCustomerFormProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Organization ID */
  orgId: string;
  /** Ledger ID */
  ledgerId: string;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when customer is successfully created */
  onCreate: (customer: Party) => void;
  /** Callback to create the customer */
  onSubmit: (data: QuickCustomerData) => Promise<Party>;
  /** Whether this is for creating a supplier instead of customer */
  mode?: 'customer' | 'supplier';
}

export interface QuickCustomerData {
  name: string;
  gstin?: string;
  email?: string;
  phone?: string;
  companyVendorCode?: string;
  /** Transaction currency — required by the backend; defaults to the base currency. */
  defaultCurrency?: string;
}

const EMPTY_FORM: QuickCustomerData = { name: '', gstin: '', email: '', phone: '', companyVendorCode: '' }

interface QuickCustomerFormState {
  formData: QuickCustomerData
  isSubmitting: boolean
  error: string
  handleChange: (field: keyof QuickCustomerData, value: string) => void
  handleSubmit: (e?: React.FormEvent) => Promise<void>
  handleClose: () => void
}

function useQuickCustomerFormState(label: string, baseCurrency: string, onSubmit: (data: QuickCustomerData) => Promise<Party>, onCreate: (customer: Party) => void, onClose: () => void): QuickCustomerFormState {
  const [formData, setFormData] = useState<QuickCustomerData>(() => ({ ...EMPTY_FORM, defaultCurrency: baseCurrency }))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string>('')

  const handleChange = useCallback((field: keyof QuickCustomerData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setError('')
  }, [])

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!formData.name.trim()) { setError(`${label} name is required`); return }
    setIsSubmitting(true); setError('')
    try {
      const newCustomer = await onSubmit({ ...formData, defaultCurrency: firstNonBlank(formData.defaultCurrency) ?? baseCurrency })
      onCreate(newCustomer)
      setFormData({ ...EMPTY_FORM, defaultCurrency: baseCurrency })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to create ${label.toLowerCase()}`)
    } finally { setIsSubmitting(false) }
  }, [formData, onSubmit, onCreate, onClose, label, baseCurrency])

  const handleClose = useCallback(() => {
    if (!isSubmitting) { setFormData({ ...EMPTY_FORM, defaultCurrency: baseCurrency }); setError(''); onClose() }
  }, [isSubmitting, onClose, baseCurrency])

  return { formData, isSubmitting, error, handleChange, handleSubmit, handleClose }
}

function FormField({ id, label, type = 'text', value, placeholder, disabled, maxLength, helpText, required, onChange }: {
  id: string; label: string; type?: string; value: string | undefined; placeholder: string
  disabled: boolean; maxLength?: number; helpText?: string; required?: boolean; onChange: (v: string) => void
}): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={id}>{label}</label>
      <Input disabled={disabled} id={id} maxLength={maxLength} placeholder={placeholder} required={required} type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
    </div>
  )
}

/**
 * QuickCustomerForm - Minimal form for adding customers on-the-fly
 */
export function QuickCustomerForm({ isOpen, onClose, onCreate, onSubmit, mode = 'customer' }: QuickCustomerFormProps): JSX.Element {
  const label = mode === 'supplier' ? 'Supplier' : 'Customer'
  const counterparty = mode === 'supplier' ? 'supplier' : 'customer'
  const baseCurrency = useBaseCurrency()
  const { currencies } = useEnabledCurrencies()
  const isMultiCurrency = currencies.length > 1
  const { formData, isSubmitting, error, handleChange, handleSubmit, handleClose } =
    useQuickCustomerFormState(label, baseCurrency, onSubmit, onCreate, onClose)

  const footer = (
    <>
      <Button disabled={isSubmitting} variant="secondary" onClick={handleClose}>Cancel</Button>
      <Button disabled={isSubmitting} variant="primary" onClick={() => { void handleSubmit() }}>
        {isSubmitting ? 'Creating...' : `Create ${label}`}
      </Button>
    </>
  )

  return (
    <Modal footer={footer} isOpen={isOpen} size="md" title={`Quick Add ${label}`} onClose={handleClose}>
      <form onSubmit={(e) => { void handleSubmit(e) }}>
        {error && (
          <div className="el-bleed el-px-xl py-2.5 -mt-[var(--el-layout-gap-xl)] mb-[var(--el-layout-gap-md)] bg-red-50 border-b border-red-200">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        <div className="flex flex-col el-gap-field">
          <FormField required disabled={isSubmitting} id="quick-customer-name" label={`${label} Name *`} placeholder={`Enter ${label.toLowerCase()} name`} type="text" value={formData.name} onChange={(v) => handleChange('name', v)} />
          <FormField disabled={isSubmitting} helpText={`The vendor code assigned to you in the ${counterparty}'s accounts`} id="quick-customer-vendor-code" label="Vendor Code (Optional)" placeholder={`Vendor code assigned by ${counterparty}`} type="text" value={formData.companyVendorCode} onChange={(v) => handleChange('companyVendorCode', v)} />
          <FormField disabled={isSubmitting} id="quick-customer-gstin" label="GSTIN (Optional)" maxLength={15} placeholder="15-digit GSTIN" type="text" value={formData.gstin} onChange={(v) => handleChange('gstin', v.toUpperCase())} />
          <FormField disabled={isSubmitting} id="quick-customer-email" label="Email (Optional)" placeholder={`${counterparty}@example.com`} type="email" value={formData.email} onChange={(v) => handleChange('email', v)} />
          <FormField disabled={isSubmitting} id="quick-customer-phone" label="Phone (Optional)" placeholder="+91 1234567890" type="tel" value={formData.phone} onChange={(v) => handleChange('phone', v)} />
          {isMultiCurrency && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="quick-customer-currency">Currency</label>
              <Select disabled={isSubmitting} id="quick-customer-currency" options={currencies} value={formData.defaultCurrency ?? baseCurrency} onChange={(v) => handleChange('defaultCurrency', v)} />
            </div>
          )}
        </div>
        <div className="el-bleed el-px-xl py-3 mt-[var(--el-layout-gap-md)] bg-blue-50 border-t border-blue-200">
          <p className="text-xs text-blue-700">This creates a basic {counterparty} record. You can add more details (addresses, credit terms, etc.) later from the Parties page.</p>
        </div>
        <button aria-hidden className="hidden" tabIndex={-1} type="submit" />
      </form>
    </Modal>
  )
}
